import React from 'react';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { AlertService } from 'components/alertService';
import { nullifyEmptyFields, useIsMounted } from 'utils';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import {
  AppErrorCode,
  MutationRemoveCardFromWorkerArgs,
  Success,
  useAddCardToWorkerMutation,
} from 'apollo/generated/client-operations';
import { CheckIcon } from 'components/icons';
import { AlreadyExistingBadgeMessage } from 'components/alreadyExistingBadgeMessage';
import { ReactivateBadgeState } from 'containers/worker/modals/badgeModal/types';
import { REMOVE_CARD_FROM_WORKER } from 'containers/worker/helpers/queries';
import {
  WorkerCard,
  WorkerCardAlreadyExistsErrorExtensions,
} from 'containers/worker/modals/badgeModal/components/types';
import {
  getFormDefaultValues,
  getFormInputs,
} from './AssignQRModalContent.forms';
import { AssignQRFormData } from './types';

export type AssignQRModalContentProps = {
  setOpen: () => void;
  onAction: (reassignQRData?: AssignQRFormData) => void;
  reactivateBadge: (reactivateBadgeState: ReactivateBadgeState) => void;
  jobsiteId: string;
  workerId: string;
  workerCardFormatId: string;
  workerCards: WorkerCard[];
};

export function AssignQRModalContent(
  props: AssignQRModalContentProps,
): React.ReactElement {
  const {
    setOpen,
    onAction,
    workerId,
    jobsiteId,
    workerCardFormatId,
    workerCards,
    reactivateBadge,
  } = props;

  const isMounted = useIsMounted();
  const [isFetching, setIsFetching] = React.useState<boolean>(false);

  const [addCardToWorker] = useAddCardToWorkerMutation();

  const [removeCardFromWorker] = useMutation<
    Success,
    MutationRemoveCardFromWorkerArgs
  >(REMOVE_CARD_FROM_WORKER, {
    onError: (error) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
      setIsFetching(false);
    },
  });

  const onSubmit: FormOnSubmit<AssignQRFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isFetching) {
      return;
    }

    setIsFetching(true);

    try {
      const { qrCodeNumber, badges } = nullifyEmptyFields(data);

      await addCardToWorker({
        variables: {
          jobsiteId,
          workerId,
          workerCardInput: {
            cardNumber: qrCodeNumber,
            workerCardFormatId,
            isTemporary: false,
            startDate: moment().toDate(),
            endDate: null,
            cardIssueReason: null,
          },
        },
      });

      const workerCardIdsToDeactivate = badges
        .filter(({ selected }) => selected)
        .map(({ workerCard }) => workerCard.workerCardId);
      if (workerCardIdsToDeactivate.length) {
        await removeCardFromWorker({
          variables: {
            workerCardIds: workerCardIdsToDeactivate,
          },
        });
      }

      onAction();
      AlertService.alert('success', 'Success', 'QR Code successfully assigned');
    } catch (error) {
      event.preventDefault();

      const workerCardAlreadyExistsError =
        getGraphQLErrorByCode<WorkerCardAlreadyExistsErrorExtensions>(
          error,
          AppErrorCode.WorkerCardAlreadyExists,
        );
      if (workerCardAlreadyExistsError) {
        const { scope, workerCardInfo, canBeReactivated } =
          workerCardAlreadyExistsError.extensions ?? {};

        if (scope === 'onDifferentWorker') {
          onAction(data);
        } else {
          const { workerCardId } = workerCardInfo?.[0] ?? {};
          formApi.setError('qrCodeNumber', {
            message: (
              <AlreadyExistingBadgeMessage
                canBeReactivated={canBeReactivated}
                errorMessage={workerCardAlreadyExistsError.message}
                onClick={(): void =>
                  reactivateBadge({
                    workerCardToReactivate: { id: workerCardId },
                    expirationDate: null,
                  })
                }
              />
            ),
            shouldFocus: true,
          });
        }
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      }
    } finally {
      if (isMounted()) {
        setIsFetching(false);
      }
    }
  };

  const formInputs = getFormInputs();
  const defaultValues = React.useMemo(
    () => getFormDefaultValues(workerCards),
    [workerCards],
  );

  return (
    <ModalFormContent
      setOpen={setOpen}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Assign QR code"
      actionIcon={CheckIcon}
      actionButtonWithSpinner={isFetching}
    />
  );
}
